// The TipTap extensions that define a contract document's content, shared by
// the editor (which adds collaboration on top) and the read-only view, so a
// document renders the same in both.
import StarterKit from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import { TextStyle, Color, FontSize, LineHeight } from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import { Extension } from "@tiptap/core";
import Paragraph from "@tiptap/extension-paragraph";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { TableMap } from "@tiptap/pm/tables";
import { CustomTableRow } from "../extensions/CustomTableRow";
import { CustomCell } from "../extensions/CustomCell";
import { CustomTableHeader } from "../extensions/CustomTableHeader";
import { PageBreak } from "./PageBreak";
import { ySyncPluginKey } from "@tiptap/y-tiptap";

// Same as the editor's: row ids for StableRowIds
function safeUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "uuid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function canEditRow(editableBy, userRoles) {
  return !!editableBy && editableBy.length > 0 &&
    userRoles.some(ur => {
      const userRoleCode = ur.role || ur;
      return editableBy.some(eb => eb.toLowerCase() === userRoleCode.toLowerCase());
    });
}

// Locks are per-user, so they are shown with decorations rather than node attributes:
// writing them into the document would sync one user's locks to every collaborator.
function buildLockDecorations(doc, userRoles) {
  const decorations = [];

  doc.descendants((node, pos) => {
    if (node.type.name !== 'tableRow') return true;

    const canEdit = canEditRow(node.attrs?.editableBy, userRoles);
    if (!canEdit) {
      let cellPos = pos + 1;
      node.forEach((cell) => {
        const cellRole = cell.attrs?.role;
        if (cellRole === 'heading' || cellRole === 'content') {
          decorations.push(Decoration.node(cellPos, cellPos + cell.nodeSize, { class: 'locked' }));
        }
        cellPos += cell.nodeSize;
      });
    }
    return true;
  });

  return DecorationSet.create(doc, decorations);
}

const LockManagement = Extension.create({
  name: 'lockManagement',

  addProseMirrorPlugins() {
    const userRoles = this.options.userRoles || [];

    return [
      new Plugin({
        key: new PluginKey('lockManagement'),

        state: {
          init: (_config, state) => buildLockDecorations(state.doc, userRoles),
          apply: (tr, decorations, _oldState, newState) =>
            tr.docChanged ? buildLockDecorations(newState.doc, userRoles) : decorations,
        },

        props: {
          decorations(state) {
            return this.getState(state);
          },
        },

        filterTransaction(tr, state) {
          if (!tr.docChanged) return true;
          // Never drop changes coming from the shared Y doc, or this editor diverges from it.
          if (tr.getMeta(ySyncPluginKey)) return true;

          let allowed = true;

          tr.steps.forEach((step, i) => {
            if (!allowed || !step.slice) return;

            const $from = tr.docs[i].resolve(step.from);

            let cellNode = null;
            let rowNode = null;

            for (let d = $from.depth; d > 0; d--) {
              const node = $from.node(d);
              if (!cellNode && (node.type.name === 'tableCell' || node.type.name === 'tableHeader')) {
                cellNode = node;
              }
              if (!rowNode && node.type.name === 'tableRow') {
                rowNode = node;
              }
              if (cellNode && rowNode) break;
            }

            if (cellNode && rowNode) {
              const cellRole = cellNode.attrs?.role;
              const editableBy = rowNode.attrs?.editableBy;

              if ((cellRole === 'heading' || cellRole === 'content') && !canEditRow(editableBy, userRoles)) {
                allowed = false;
              }
            }
          });

          return allowed;
        },
      }),
    ];
  },
});

function findTableFromSelection(selection) {
  for (let d = selection.$from.depth; d > 0; d--) {
    const node = selection.$from.node(d);
    if (node.type?.spec?.tableRole === "table") {
      return { node, pos: selection.$from.before(d) };
    }
  }
  return null;
}

function findCellFromSelection(selection) {
  for (let d = selection.$from.depth; d > 0; d--) {
    const node = selection.$from.node(d);
    const role = node.type?.spec?.tableRole;
    if (role === "cell" || role === "header_cell") {
      return { node, pos: selection.$from.before(d) };
    }
  }
  return null;
}

const CustomTable = Table.extend({
  addExtensions() {
    return [CustomTableRow, CustomCell, CustomTableHeader];
  },
  addKeyboardShortcuts() {
    const parent = this.parent?.() ?? {};
    return {
      ...parent,
      Tab: ({ editor }) => {
        const { state } = editor;
        const tableInfo = findTableFromSelection(state.selection);
        if (!tableInfo) return parent.Tab?.({ editor }) ?? false;

        const cellInfo = findCellFromSelection(state.selection);
        if (!cellInfo) return parent.Tab?.({ editor }) ?? false;

        const map = TableMap.get(tableInfo.node);
        const tableStart = tableInfo.pos + 1;
        const rect = map.findCell(cellInfo.pos - tableStart);
        const isLastCell = rect.left === map.width - 1 && rect.top === map.height - 1;

        if (!isLastCell) return parent.Tab?.({ editor }) ?? false;

        const after = tableInfo.pos + tableInfo.node.nodeSize;
        let tr = state.tr;
        const nextNode = state.doc.nodeAt(after);
        if (!nextNode) {
          const para = state.schema.nodes.paragraph;
          if (para) tr = tr.insert(after, para.createAndFill());
        }
        const selPos = Math.min(after + 1, tr.doc.content.size);
        tr = tr.setSelection(TextSelection.create(tr.doc, selPos)).scrollIntoView();
        editor.view.dispatch(tr);
        return true;
      },
      ArrowUp: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;

        const $pos = selection.$from;
        const cellInfo = findCellFromSelection(selection);
        if (!cellInfo) return parent.ArrowUp?.({ editor }) ?? false;

        const cellStart = cellInfo.pos + 1;
        if ($pos.pos !== cellStart) return parent.ArrowUp?.({ editor }) ?? false;

        const result = parent.ArrowUp?.({ editor }) ?? false;

        setTimeout(() => {
          const newState = editor.state;
          const newSelection = newState.selection;
          const newCellInfo = findCellFromSelection(newSelection);

          if (newCellInfo) {
            const cellEnd = newCellInfo.pos + newCellInfo.node.nodeSize - 1;
            const tr = newState.tr.setSelection(TextSelection.create(newState.doc, cellEnd));
            editor.view.dispatch(tr);
          }
        }, 0);

        return result;
      },
      "Mod-a": ({ editor }) => {
        const { state } = editor;
        const cellInfo = findCellFromSelection(state.selection);

        if (!cellInfo) return parent["Mod-a"]?.({ editor }) ?? false;

        const cellStart = cellInfo.pos + 1;
        const cellEnd = cellInfo.pos + cellInfo.node.nodeSize - 1;

        const tr = state.tr.setSelection(TextSelection.create(state.doc, cellStart, cellEnd));
        editor.view.dispatch(tr);

        return true;
      },
    };
  },
});

const StableRowIds = Extension.create({
  name: "stableRowIds",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction(transactions, oldState, state) {
          const rowPositionsById = new Map();
          const rowsNeedingIds = [];

          state.doc.descendants((node, pos) => {
            if (node.type.name !== "tableRow") return true;
            const id = node.attrs?.id;
            if (!id) {
              rowsNeedingIds.push({ pos, isCopy: false });
            } else {
              rowPositionsById.set(id, [...(rowPositionsById.get(id) || []), pos]);
            }
            return true;
          });

          // A copied, pasted or split row carries the original's id. Keep the id on the row the
          // original moved to, and give the copies new ids so they don't share codifications.
          const duplicateIds = [...rowPositionsById.keys()].filter(id => rowPositionsById.get(id).length > 1);
          if (duplicateIds.length > 0) {
            const oldPositionById = new Map();
            oldState.doc.descendants((node, pos) => {
              if (node.type.name === "tableRow" && node.attrs?.id && !oldPositionById.has(node.attrs.id)) {
                oldPositionById.set(node.attrs.id, pos);
              }
              return true;
            });

            for (const id of duplicateIds) {
              const positions = rowPositionsById.get(id);
              let originalPos = oldPositionById.get(id);
              if (originalPos !== undefined) {
                originalPos = transactions.reduce((p, t) => t.mapping.map(p), originalPos);
              }
              const keepPos = positions.includes(originalPos) ? originalPos : positions[0];
              positions.filter(p => p !== keepPos).forEach(pos => rowsNeedingIds.push({ pos, isCopy: true }));
            }
          }

          if (rowsNeedingIds.length === 0) return null;

          const tr = state.tr;
          for (const { pos, isCopy } of rowsNeedingIds) {
            const node = tr.doc.nodeAt(pos);
            // A copy hasn't been codified yet, whatever status the original had
            const codifyStatus = isCopy ? "idle" : node.attrs.codifyStatus;
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, id: safeUuid(), codifyStatus });
          }
          return tr;
        },
      }),
    ];
  },
});

/**
 * The document's content extensions, as the editor has always used them. The
 * editor passes the user's roles (for row locks) and adds Collaboration /
 * CollaborationCaret on top; the read-only view uses them on their own.
 */
export const documentExtensions = ({ userRoles = [] } = {}) => [
  StarterKit.configure({
    paragraph: false,
    undoRedo: false, // Disabled - Collaboration extension provides its own history (undo/redo)
    blockquote: {
      HTMLAttributes: {
        class: 'blockquote-content',
      },
    },
    link: {
      openOnClick: false,
      HTMLAttributes: {
        class: 'custom-link',
      },
    },
  }),
  Paragraph.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        textIndent: {
          default: null,
          parseHTML: element => element.style.textIndent || null,
          renderHTML: attributes => {
            if (!attributes.textIndent) {
              return {};
            }
            return {
              style: `text-indent: ${attributes.textIndent}`,
            };
          },
        },
      };
    },
  }),
  PageBreak,
  // fontSize/lineHeight are attributes on the textStyle mark, as before, so saved documents
  // load unchanged
  TextStyle,
  FontSize,
  LineHeight,
  TextAlign.configure({
    types: ['heading', 'paragraph'],
    alignments: ['left', 'center', 'right', 'justify'],
  }),
  CustomTable.configure({ resizable: true, handleWidth: 6 }),
  StableRowIds,
  Color,
  Highlight.configure({
    multicolor: true,
  }),
  LockManagement.configure({
    userRoles,
  }),
];
